import {
  Button,
  Typography,
  Box,
  LinearProgress,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Upload, CheckCircle, Download } from "@mui/icons-material";
import { InstructionSection } from "../InstructionSection";
import { useAccountInfo } from "../../../hooks/useAccountInfo";
import { useFileProcessor } from "../hooks/useFileProcessor";
import theme from "../../../theme";

const FileProgressIndicator = ({
  fileName,
  progress,
  uploadComplete,
  disabled,
}) => (
  <Box sx={{ mt: 1 }}>
    <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
      {fileName}
    </Typography>
    <LinearProgress
      variant="determinate"
      value={progress}
      sx={{
        height: 8,
        borderRadius: 4,
        backgroundColor: theme.palette.gray.medium,
        "& .MuiLinearProgress-bar": {
          backgroundColor: disabled
            ? theme.palette.gray.medium
            : uploadComplete
              ? theme.palette.secondary.lawnGreen
              : theme.palette.primary.main,
        },
      }}
    />
  </Box>
);

const FileUploadForm = ({ handleFileUpload, uploadComplete, disabled }) => (
  <>
    <input
      type="file"
      id="file-upload"
      style={{ display: "none" }}
      onChange={handleFileUpload}
      accept=".xlsx"
      disabled={disabled}
    />
    <label htmlFor="file-upload">
      <Button
        component="span"
        variant="contained"
        fullWidth
        disabled={disabled}
        startIcon={uploadComplete ? <CheckCircle /> : <Upload />}
        sx={{
          backgroundColor: disabled
            ? theme.palette.gray.dark
            : theme.palette.primary.main,
          "&:hover": { backgroundColor: theme.palette.primary.main },
          mb: 1,
        }}
      >
        {uploadComplete ? "Upload Complete" : "Upload File"}
      </Button>
    </label>
  </>
);

const FileProcessing = ({ disabled, onProcess, processLoading, onGoBack }) => (
  <Box sx={{ my: 2 }}>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Button
        disabled={disabled}
        onClick={onProcess}
        variant="contained"
        sx={{
          width: "100%",
          backgroundColor: theme.palette.primary.main,
          "&:hover": {
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.primary.white,
          },
          "&:disabled": {
            backgroundColor: theme.palette.gray.dark,
            color: theme.palette.primary.white,
            border: "none",
          },
        }}
        startIcon={
          processLoading ? <CircularProgress size={20} color="inherit" /> : null
        }
      >
        {processLoading ? "Processing..." : "Process File"}
      </Button>

      <Button
        disabled={processLoading}
        onClick={onGoBack}
        variant="contained"
        sx={{
          width: "100%",
          backgroundColor: theme.palette.primary.white,
          border: `1px solid ${theme.palette.primary.main}`,
          color: theme.palette.primary.main,
          boxShadow: "none",
          "&:hover": {
            backgroundColor: theme.palette.primary.medium,
            color: theme.palette.primary.brandSecondary,
            border: `1px solid ${theme.palette.primary.brandSecondary}`,
          },
          "&:disabled": {
            backgroundColor: theme.palette.gray.medium,
            border: "none",
          },
        }}
      >
        Go Back
      </Button>
    </Box>
  </Box>
);

const ProcessingCompletedScreen = ({
  handleDownload,
  onGoBack,
  onBackToToolSelection,
}) => (
  <Box sx={{ mt: 3 }}>
    <Alert
      severity="success"
      sx={{ mb: 2, alignItems: "center" }}
      icon={<CheckCircle />}
    >
      <Typography variant="subtitle1" component="div">
        File Processed Successfully!
      </Typography>
      <Typography variant="body2">
        Your file has been processed and is ready for download.
      </Typography>
    </Alert>
    <Button
      onClick={handleDownload}
      variant="contained"
      startIcon={<Download />}
      sx={{
        backgroundColor: theme.palette.primary.main,
        "&:hover": { backgroundColor: theme.palette.primary.main },
        mb: 1,
      }}
      fullWidth
    >
      Download Processed File
    </Button>
    <Button
      onClick={onGoBack}
      variant="contained"
      fullWidth
      sx={{
        width: "100%",
        backgroundColor: theme.palette.primary.white,
        border: `1px solid ${theme.palette.primary.main}`,
        color: theme.palette.primary.main,
        boxShadow: "none",
        mb: 1,
        "&:hover": {
          backgroundColor: theme.palette.primary.medium,
          color: theme.palette.primary.brandSecondary,
          border: `1px solid ${theme.palette.primary.brandSecondary}`,
        },
      }}
    >
      Process Another File
    </Button>
    <Button
      onClick={onBackToToolSelection}
      variant="text"
      fullWidth
      sx={{
        width: "100%",
        color: theme.palette.primary.main,
      }}
    >
      Back to Tool Selection
    </Button>
  </Box>
);

/**
 Generic upload → process → download panel.
 Reused by Translator, Supply Constants, Demand Constants...
 */
export function FileProcessorPanel({
  title,
  toolType,
  activityKey,
  processFn,
  downloadFileName,
  toolSelectionRoute,
}) {
  const navigate = useNavigate();
  const { accountInfo } = useAccountInfo();
  const {
    file,
    loading,
    progress,
    uploadComplete,
    currentStep,
    completedSteps,
    processComplete,
    handleCompletedSteps,
    handleFileUpload,
    processFile,
    goBackToStep1,
  } = useFileProcessor({
    processFn,
    activityKey,
    accountId: accountInfo?.accountId,
    downloadFileName,
  });

  const handleBackToToolSelection = () => {
    if (toolSelectionRoute) navigate(toolSelectionRoute);
  };

  return (
    <>
      <Typography variant="h4" sx={{ mb: 4, py: 2, textAlign: "center" }}>
        {title}
      </Typography>
      <InstructionSection
        currentStep={currentStep}
        completedSteps={completedSteps}
        toolType={toolType}
      />
      <Box
        sx={{
          m: "auto",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr", md: "repeat(3, 1fr)" },
          alignItems: "stretch",
          gap: 2,
          p: 2,
          my: 4,
          maxWidth: { xs: "400px", sm: "420px", md: "75rem" },
        }}
      >
        {/* Step 1: Upload */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: 2,
            height: "100%",
            backgroundColor: theme.palette.primary.lightBackground,
            borderRadius: 2,
            opacity: currentStep === 1 ? 1 : completedSteps.has(1) ? 0.6 : 0.4,
            pointerEvents: currentStep === 1 ? "auto" : "none",
          }}
        >
          <Typography variant="h6" sx={{ textAlign: "center" }}>
            Upload Your Excel File
          </Typography>
          <FileUploadForm
            handleFileUpload={handleFileUpload}
            disabled={currentStep !== 1}
          />
          {file && (
            <FileProgressIndicator
              fileName={file.name}
              progress={progress}
              uploadComplete={uploadComplete}
              disabled={currentStep !== 1}
            />
          )}
        </Box>

        {/* Step 2: Process */}
        {uploadComplete && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              p: 2,
              height: "100%",
              backgroundColor: theme.palette.primary.lightBackground,
              borderRadius: 2,
              opacity:
                currentStep === 2 ? 1 : completedSteps.has(2) ? 0.6 : 0.4,
              pointerEvents: currentStep === 2 ? "auto" : "none",
            }}
          >
            <Typography variant="h6" sx={{ textAlign: "center" }}>
              Process File
            </Typography>
            <FileProcessing
              disabled={currentStep !== 2 || !file}
              onProcess={processFile}
              processLoading={loading}
              onGoBack={goBackToStep1}
            />
          </Box>
        )}

        {/* Step 3: Download */}
        {processComplete && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              p: 2,
              height: "100%",
              backgroundColor: theme.palette.primary.lightBackground,
              borderRadius: 2,
              opacity: currentStep === 3 ? 1 : 0.4,
              pointerEvents: currentStep === 3 ? "auto" : "none",
            }}
          >
            <Typography variant="h6" sx={{ textAlign: "center" }}>
              Download Results
            </Typography>
            <ProcessingCompletedScreen
              handleDownload={handleCompletedSteps}
              onGoBack={goBackToStep1}
              onBackToToolSelection={handleBackToToolSelection}
            />
          </Box>
        )}
      </Box>
    </>
  );
}
